import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './PracticeOptionsModal.css';

// SVG icons to replace emoji
const IconSmart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z" />
    <path d="M9 21h6" />
    <path d="M10 17v4" />
    <path d="M14 17v4" />
  </svg>
);

const IconTarget = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const IconSettings = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const IconStar = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

interface VocabularyItem {
  id: string;
  masteryLevel: number;
  isFavorite: boolean;
}

export type LearningMode = 'speaking' | 'dictation' | 'meaning' | 'fillBlank';

interface PracticeOptions {
  mode: 'smart' | 'weak' | 'custom';
  count: number;
  masteryLevels: number[];
  favoritesOnly: boolean;
  learningModes: LearningMode[];
}

interface PracticeOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: (options: PracticeOptions) => void;
  vocabularyList: VocabularyItem[];
}

const MASTERY_COLORS = ['#dc3545', '#fd7e14', '#ffc107', '#20c997', '#28a745', '#667eea'];

const PracticeOptionsModal: React.FC<PracticeOptionsModalProps> = ({
  isOpen,
  onClose,
  onStart,
  vocabularyList,
}) => {
  const { t } = useTranslation('vocabulary');
  const masteryLabels = t('practice.masteryLabels', { returnObjects: true }) as string[];
  const [mode, setMode] = useState<'smart' | 'weak' | 'custom'>('smart');
  const [count, setCount] = useState<number>(20);
  const [selectedLevels, setSelectedLevels] = useState<number[]>([0, 1, 2, 3, 4, 5]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [learningModes, setLearningModes] = useState<LearningMode[]>(['speaking', 'dictation', 'meaning', 'fillBlank']);

  // Reset to defaults when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('smart');
      setCount(20);
      setSelectedLevels([0, 1, 2, 3, 4, 5]);
      setFavoritesOnly(false);
      setLearningModes(['speaking', 'dictation', 'meaning', 'fillBlank']);
    }
  }, [isOpen]);

  const toggleLearningMode = (lm: LearningMode) => {
    setLearningModes(prev => {
      const next = prev.includes(lm) ? prev.filter(m => m !== lm) : [...prev, lm];
      return next.length === 0 ? prev : next; // prevent empty selection
    });
  };

  // Calculate preview based on current options
  const preview = useMemo(() => {
    let filtered = [...vocabularyList];

    // Apply favorites filter
    if (favoritesOnly) {
      filtered = filtered.filter(v => v.isFavorite);
    }

    // Apply mastery filter (only for custom mode)
    if (mode === 'custom') {
      filtered = filtered.filter(v => selectedLevels.includes(v.masteryLevel));
    } else if (mode === 'weak') {
      filtered = filtered.filter(v => v.masteryLevel <= 2);
    }

    // Count by mastery level
    const byLevel: Record<number, number> = {};
    filtered.forEach(v => {
      byLevel[v.masteryLevel] = (byLevel[v.masteryLevel] || 0) + 1;
    });

    // Smart selection preview
    const selected: { level: number; count: number }[] = [];
    let remaining = count;

    if (mode === 'smart') {
      // Prioritize low mastery
      for (let level = 0; level <= 5 && remaining > 0; level++) {
        const available = byLevel[level] || 0;
        const take = Math.min(available, remaining);
        if (take > 0) {
          selected.push({ level, count: take });
          remaining -= take;
        }
      }
    } else {
      // Equal distribution for weak/custom
      const levels = mode === 'weak' ? [0, 1, 2] : selectedLevels;
      levels.sort((a, b) => a - b);
      for (const level of levels) {
        const available = byLevel[level] || 0;
        const take = Math.min(available, remaining);
        if (take > 0) {
          selected.push({ level, count: take });
          remaining -= take;
        }
        if (remaining <= 0) break;
      }
    }

    return {
      total: filtered.length,
      selected,
      willPractice: count - remaining,
    };
  }, [vocabularyList, mode, count, selectedLevels, favoritesOnly]);

  const toggleLevel = (level: number) => {
    setSelectedLevels(prev =>
      prev.includes(level)
        ? prev.filter(l => l !== level)
        : [...prev, level].sort()
    );
  };

  const handleStart = () => {
    onStart({
      mode,
      count,
      masteryLevels: mode === 'custom' ? selectedLevels : mode === 'weak' ? [0, 1, 2] : [0, 1, 2, 3, 4, 5],
      favoritesOnly,
      learningModes,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="practice-modal-overlay" onClick={onClose}>
      <div className="practice-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="practice-modal-header">
          <h2>{t('practice.settings')}</h2>
          <button className="close-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* Mode Selection */}
        <div className="practice-section">
          <div className="mode-selector">
            <button
              className={`mode-btn ${mode === 'smart' ? 'active' : ''}`}
              onClick={() => setMode('smart')}
            >
              <span className="mode-icon"><IconSmart /></span>
              <span className="mode-label">{t('practice.modeSmartLabel')}</span>
              <span className="mode-desc">{t('practice.modeSmartDesc')}</span>
            </button>
            <button
              className={`mode-btn ${mode === 'weak' ? 'active' : ''}`}
              onClick={() => setMode('weak')}
            >
              <span className="mode-icon"><IconTarget /></span>
              <span className="mode-label">{t('practice.modeWeakLabel')}</span>
              <span className="mode-desc">{t('practice.modeWeakDesc')}</span>
            </button>
            <button
              className={`mode-btn ${mode === 'custom' ? 'active' : ''}`}
              onClick={() => setMode('custom')}
            >
              <span className="mode-icon"><IconSettings /></span>
              <span className="mode-label">{t('practice.modeCustomLabel')}</span>
              <span className="mode-desc">{t('practice.modeCustomDesc')}</span>
            </button>
          </div>
        </div>

        {/* Practice Count */}
        <div className="practice-section">
          <label className="section-label">{t('practice.countLabel')}</label>
          <div className="count-selector">
            {[10, 20, 30, 50].map(n => (
              <button
                key={n}
                className={`count-btn ${count === n ? 'active' : ''}`}
                onClick={() => setCount(n)}
              >
                {n}
              </button>
            ))}
            <button
              className={`count-btn ${count === 999 ? 'active' : ''}`}
              onClick={() => setCount(999)}
            >
              {t('practice.countAll')}
            </button>
          </div>
        </div>

        {/* Custom Filters (only show in custom mode) */}
        {mode === 'custom' && (
          <div className="practice-section">
            <label className="section-label">{t('practice.masteryFilter')}</label>
            <div className="mastery-chips">
              {masteryLabels.map((label, level) => (
                <button
                  key={level}
                  className={`mastery-chip ${selectedLevels.includes(level) ? 'selected' : ''}`}
                  style={{
                    '--chip-color': MASTERY_COLORS[level],
                  } as React.CSSProperties}
                  onClick={() => toggleLevel(level)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Learning Modes */}
        <div className="practice-section">
          <label className="section-label">
            {t('practice.learningModes')}
            <span className="section-hint">{t('practice.learningModesHint')}</span>
          </label>
          <div className="learning-mode-chips">
            {([
              { id: 'speaking' as LearningMode, icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                </svg>
              )},
              { id: 'dictation' as LearningMode, icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
              )},
              { id: 'meaning' as LearningMode, icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z"/>
                </svg>
              )},
              { id: 'fillBlank' as LearningMode, icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
              )},
            ]).map(({ id, icon }) => (
              <button
                key={id}
                className={`learning-mode-chip ${learningModes.includes(id) ? 'selected' : ''}`}
                onClick={() => toggleLearningMode(id)}
              >
                <span className="lm-chip-icon">{icon}</span>
                <span className="lm-chip-label">{t(`practice.mode${id.charAt(0).toUpperCase() + id.slice(1)}` as any)}</span>
                <span className="lm-chip-desc">{t(`practice.mode${id.charAt(0).toUpperCase() + id.slice(1)}Desc` as any)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Favorites Filter */}
        <div className="practice-section">
          <label className="favorites-toggle">
            <input
              type="checkbox"
              checked={favoritesOnly}
              onChange={e => setFavoritesOnly(e.target.checked)}
            />
            <span className="toggle-label">{t('practice.favoritesOnly')}</span>
            <span className="toggle-star"><IconStar /></span>
          </label>
        </div>

        {/* Preview */}
        <div className="practice-preview">
          <div className="preview-title">{t('practice.previewTitle', { count: preview.willPractice })}</div>
          {preview.selected.length > 0 && (
            <div className="preview-breakdown">
              {preview.selected.map(({ level, count }) => (
                <span
                  key={level}
                  className="preview-item"
                  style={{ backgroundColor: MASTERY_COLORS[level] + '20', color: MASTERY_COLORS[level] }}
                >
                  {t('practice.previewItem', { count, level: masteryLabels[level] })}
                </span>
              ))}
            </div>
          )}
          {preview.willPractice === 0 && (
            <div className="preview-empty">
              {t('practice.previewEmpty')}
            </div>
          )}
        </div>

        {/* Start Button */}
        <button
          className="start-practice-btn"
          onClick={handleStart}
          disabled={preview.willPractice === 0}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M8 5v14l11-7z"/>
          </svg>
          {t('practice.startPractice')}
        </button>
      </div>
    </div>
  );
};

export default PracticeOptionsModal;
